/**
 * Subscription Service
 * Handles subscription status checking, promotions, and checkout flow
 */

import { telegram } from './telegram';

const API_BASE = '/api/subscription';
const SUBSCRIPTION_GRACE_STORAGE_KEY = 'ziawellnav_subscription_last_good';
const SUBSCRIPTION_GRACE_TTL_MS = 24 * 60 * 60 * 1000;

export interface SubscriptionStatus {
  isSubscribed: boolean;
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | 'error';
  type?: 'monthly' | 'yearly' | 'business_plus';
  rawPlan?: string;
  expiresAt?: string;
  isTrialing: boolean;
  trialDaysRemaining: number;
  canTrial: boolean;
  checkedAt?: string;
  lastError?: string;
  source?: 'cache' | 'api' | 'http' | 'network' | 'parse';
  httpStatus?: number;
  hint?: string;
  accessSource?: 'individual_subscription' | 'trial' | 'stars_individual' | 'business_seat' | 'stars_business_seat' | 'none' | string;
  paymentRequired?: boolean;
  businessSeat?: {
    companyId: string;
    purchasedSeats: number;
    stripeSeats: number;
    starsSeats: number;
    source: string;
  } | null;
  starsPaywall?: {
    provider: 'telegram_stars';
    accessMode?: 'soft' | 'hard' | string;
    paymentRequired?: boolean;
    softLaunch?: boolean;
    currency: 'XTR';
    monthly: { productType: string; starsAmount: number; durationDays: number; label: string };
    yearly?: { productType: string; starsAmount: number; durationDays: number; label: string };
    message: string;
  } | null;
  // Business Plus specific
  isBusinessPlus?: boolean;
  businessPlusFeatures?: string[];
  companyName?: string;
  companyRole?: string;
}

export interface StarsInvoiceResult {
  invoiceId: string;
  invoiceLink: string;
  invoicePayload: string;
  productType: string;
  starsAmount: number;
  seatCount?: number | null;
  planId?: string | null;
  durationDays: number;
}

export interface PromoValidation {
  valid: boolean;
  code?: string;
  description?: string;
  discountType?: 'percent' | 'fixed';
  discountValue?: number;
  discountDisplay?: string;
  appliesTo?: 'all' | 'monthly' | 'yearly';
  expiresAt?: string;
  error?: string;
}

// Plan ID types - includes both new and legacy IDs for backward compatibility
export type NewPlanId = 'individual_monthly' | 'individual_yearly' | 'business_starter' | 'business_team' | 'business_fleet' | 'business_enterprise';
export type LegacyPlanId = 'monthly' | 'yearly' | 'business_plus';
export type PlanId = NewPlanId | LegacyPlanId;

// Map legacy IDs to new IDs
export const LEGACY_PLAN_MAP: Record<LegacyPlanId, NewPlanId> = {
  'monthly': 'individual_monthly',
  'yearly': 'individual_yearly', 
  'business_plus': 'business_starter'
};

export interface PricingPlan {
  id: PlanId;
  name: string;
  price: number;
  originalPrice?: number;
  interval: 'month' | 'year';
  trialDays: number;
  features: string[];
  popular?: boolean;
  savings?: string;
  tier: 'individual' | 'business';
  badge?: string;
  minUsers?: number;
  maxUsers?: number;
  charityPercent?: number; // % going to Weekend Hunger Initiative
}

export interface BusinessTier {
  id: 'starter' | 'team' | 'fleet' | 'enterprise';
  name: string;
  pricePerUser: number;
  minUsers: number;
  maxUsers: number | null; // null = unlimited
  charityPercent: number;
}

export const MIN_BUSINESS_LICENSES = 5;
export const MAX_BUSINESS_LICENSES = 10000;
export const BUSINESS_DOC_FEE_MONTHLY = 500;
export const BUSINESS_DOC_FEE_YEARLY = 5000;
export const BUSINESS_DOC_FEE_CHARITY_PERCENT = 20;
export const CUSTOM_BOT_SETUP_FEE = 1000;
export const CYBER_CLASS_6H_PRICE = 95;
export const APPLICATION_SUPPORT_OVERAGE_RATE = 75;

export interface ApplicationSupportPricing {
  tierId: 'support_5_25' | 'support_26_100' | 'support_101_250' | 'support_251_plus';
  label: string;
  seatRangeLabel: string;
  billingCycle: 'monthly' | 'yearly';
  amount: number;
  includedHoursMonthly: number;
  overageRate: number;
  noRollover: boolean;
}

export type BusinessServicePlanId =
  | 'tech_support_hourly'
  | 'software_dev_hourly'
  | 'drone_photos'
  | 'drone_video_unedited'
  | 'ai_integration_assessment';

export const BUSINESS_TIERS: BusinessTier[] = [
  { id: 'starter', name: 'Starter', pricePerUser: 7, minUsers: 5, maxUsers: 25, charityPercent: 20 },
  { id: 'team', name: 'Team', pricePerUser: 6, minUsers: 26, maxUsers: 100, charityPercent: 20 },
  { id: 'fleet', name: 'Fleet', pricePerUser: 5, minUsers: 101, maxUsers: 250, charityPercent: 20 },
  { id: 'enterprise', name: 'Enterprise', pricePerUser: 4, minUsers: 251, maxUsers: null, charityPercent: 20 },
];

export function getBusinessTierForUsers(userCount: number): BusinessTier | null {
  if (userCount < MIN_BUSINESS_LICENSES) return null;
  return BUSINESS_TIERS.find(tier => 
    userCount >= tier.minUsers && (tier.maxUsers === null || userCount <= tier.maxUsers)
  ) || BUSINESS_TIERS[BUSINESS_TIERS.length - 1]; // Default to enterprise for 251+
}

export function calculateBusinessPrice(
  userCount: number,
  billingCycle: 'monthly' | 'yearly' = 'yearly'
): {
  tier: BusinessTier;
  total: number;
  perUser: number;
  documentationFee: number;
  grandTotal: number;
  documentationFeeLabel: string;
  seatCharityContribution: number;
  documentationFeeCharityContribution: number;
  charityContribution: number;
  charityContributionLabel: string;
  interval: 'month' | 'year';
} | null {
  const tier = getBusinessTierForUsers(userCount);
  if (!tier) return null;

  const perUser = billingCycle === 'monthly'
    ? tier.pricePerUser
    : tier.pricePerUser * 10; // 2 months free on annual
  const documentationFee = billingCycle === 'monthly'
    ? BUSINESS_DOC_FEE_MONTHLY
    : BUSINESS_DOC_FEE_YEARLY;
  const seatSubtotal = perUser * userCount;
  const seatCharityPercent = billingCycle === 'monthly' ? 10 : 20;
  const seatCharityContribution = seatSubtotal * (seatCharityPercent / 100);
  const documentationFeeCharityContribution = documentationFee * (BUSINESS_DOC_FEE_CHARITY_PERCENT / 100);
  const charityContribution = seatCharityContribution + documentationFeeCharityContribution;

  return {
    tier,
    perUser,
    total: seatSubtotal,
    documentationFee,
    grandTotal: seatSubtotal + documentationFee,
    documentationFeeLabel: billingCycle === 'monthly' ? '$500/mo documentation fee' : '$5,000/yr documentation fee',
    seatCharityContribution,
    documentationFeeCharityContribution,
    charityContribution,
    charityContributionLabel: billingCycle === 'monthly'
      ? '10% of seats + 20% of documentation fee'
      : '20% of seats + 20% of documentation fee',
    interval: billingCycle === 'monthly' ? 'month' : 'year'
  };
}

export function calculateApplicationSupportPrice(
  userCount: number,
  billingCycle: 'monthly' | 'yearly' = 'yearly'
): ApplicationSupportPricing | null {
  if (userCount < MIN_BUSINESS_LICENSES || userCount > MAX_BUSINESS_LICENSES) return null;
  const tiers: Array<Omit<ApplicationSupportPricing, 'billingCycle' | 'amount' | 'overageRate' | 'noRollover'> & { monthly: number; yearly: number; min: number; max: number | null }> = [
    { tierId: 'support_5_25', label: 'Application Support', seatRangeLabel: '5-25 seats', min: 5, max: 25, monthly: 99, yearly: 990, includedHoursMonthly: 1 },
    { tierId: 'support_26_100', label: 'Application Support', seatRangeLabel: '26-100 seats', min: 26, max: 100, monthly: 199, yearly: 1990, includedHoursMonthly: 2 },
    { tierId: 'support_101_250', label: 'Application Support', seatRangeLabel: '101-250 seats', min: 101, max: 250, monthly: 399, yearly: 3990, includedHoursMonthly: 4 },
    { tierId: 'support_251_plus', label: 'Application Support', seatRangeLabel: '251+ seats', min: 251, max: null, monthly: 799, yearly: 7990, includedHoursMonthly: 8 },
  ];
  const tier = tiers.find((item) => userCount >= item.min && (item.max === null || userCount <= item.max));
  if (!tier) return null;
  return {
    tierId: tier.tierId,
    label: tier.label,
    seatRangeLabel: tier.seatRangeLabel,
    billingCycle,
    amount: billingCycle === 'yearly' ? tier.yearly : tier.monthly,
    includedHoursMonthly: tier.includedHoursMonthly,
    overageRate: APPLICATION_SUPPORT_OVERAGE_RATE,
    noRollover: true,
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getTelegramPlatform(): string {
  try {
    return String(telegram?.webApp?.platform || '');
  } catch {
    return '';
  }
}

function parseTelegramUserFromInitData(initData: string): any | null {
  if (!initData) return null;
  try {
    const userParam = new URLSearchParams(initData).get('user');
    if (!userParam) return null;
    const parsed = JSON.parse(userParam);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
}

class SubscriptionService {
  private cachedStatus: SubscriptionStatus | null = null;
  private cacheTimestamp: number = 0;
  private cacheDuration = 60000; // 1 minute cache

  private rememberGraceStatus(status: SubscriptionStatus) {
    if (!status.isSubscribed && status.status !== 'trialing') return;
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SUBSCRIPTION_GRACE_STORAGE_KEY, JSON.stringify({
        status,
        savedAt: Date.now(),
      }));
    } catch {
      // Local cache is best effort only.
    }
  }

  getCachedGraceStatus(maxAgeMs = SUBSCRIPTION_GRACE_TTL_MS): SubscriptionStatus | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(SUBSCRIPTION_GRACE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { status?: SubscriptionStatus; savedAt?: number };
      if (!parsed.status || typeof parsed.savedAt !== 'number') return null;
      if (Date.now() - parsed.savedAt > maxAgeMs) return null;
      if (!parsed.status.isSubscribed && parsed.status.status !== 'trialing') return null;
      return { ...parsed.status, source: 'cache' };
    } catch {
      return null;
    }
  }

  private clearGraceStatus() {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(SUBSCRIPTION_GRACE_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  private getUserHeaders() {
    const user = telegram.getUser();
    return {
      'X-Telegram-User-Id': telegram.getUserId() || '',
      'X-Telegram-Username': user?.username || user?.first_name || '',
      'X-Telegram-Init-Data': telegram.getInitData()
    };
  }

  private getStatusUserHeaders() {
    const initData = telegram.getInitData();
    const initUser = parseTelegramUserFromInitData(initData);
    const liveUser = telegram.getUser();
    const hasLiveInitData = initData.length > 0;

    // For live Telegram WebApp checks, prefer initData-derived IDs so a stale
    // localStorage fallback cannot accidentally verify the wrong Android user.
    const userId = initUser?.id != null
      ? String(initUser.id)
      : liveUser?.id != null
        ? String(liveUser.id)
        : hasLiveInitData
          ? ''
          : telegram.getUserId() || '';

    const username = initUser?.username || liveUser?.username || initUser?.first_name || liveUser?.first_name || '';

    return {
      'X-Telegram-User-Id': userId,
      'X-Telegram-Username': username,
      'X-Telegram-Init-Data': initData,
    };
  }

  private async getStatusHeadersWithAndroidWait() {
    let headers = this.getStatusUserHeaders();
    const platform = getTelegramPlatform().toLowerCase();
    const shouldWait = platform.startsWith('android')
      && !headers['X-Telegram-Init-Data'];

    if (!shouldWait) return headers;

    for (const delay of [250, 500, 750]) {
      await sleep(delay);
      headers = this.getStatusUserHeaders();
      if (headers['X-Telegram-Init-Data']) break;
    }

    return headers;
  }

  private buildSubscriptionDiagnostics(headers: Record<string, string>, source: SubscriptionStatus['source'], httpStatus?: number, contentType?: string) {
    return {
      platform: getTelegramPlatform() || 'unknown',
      userIdPresent: Boolean(headers['X-Telegram-User-Id']),
      initDataPresent: Boolean(headers['X-Telegram-Init-Data']),
      source,
      httpStatus,
      contentType,
    };
  }

  /**
   * Get current subscription status
   */
  async getStatus(forceRefresh = false, signal?: AbortSignal): Promise<SubscriptionStatus> {
    // Return cached status if valid
    if (!forceRefresh && this.cachedStatus && Date.now() - this.cacheTimestamp < this.cacheDuration) {
      return { ...this.cachedStatus, source: 'cache' };
    }

    const checkedAt = new Date().toISOString();
    const headers = await this.getStatusHeadersWithAndroidWait();
    try {
      const response = await fetch(`${API_BASE}/status`, {
        headers,
        signal,
      });

      let data: any;
      const contentType = response.headers.get('content-type') || '';
      try {
        if (!contentType.includes('application/json')) {
          const body = await response.text().catch(() => '');
          const geoBlocked = response.status === 403 && /outside the US|VPN/i.test(body);
          const lastError = geoBlocked
            ? 'Subscription verification was blocked by the location check. Turn off VPN or use a US-based connection and retry.'
            : `Subscription service returned ${contentType || 'a non-JSON response'}.`;
          console.warn('[Subscription] Non-JSON status response:', this.buildSubscriptionDiagnostics(headers, 'parse', response.status, contentType));
          return {
            isSubscribed: false,
            status: 'error',
            isTrialing: false,
            trialDaysRemaining: 0,
            canTrial: false,
            checkedAt,
            lastError,
            hint: geoBlocked ? 'vpn-or-location-check' : 'non-json-response',
            source: 'parse',
            httpStatus: response.status,
          };
        }
        data = await response.json();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid subscription response';
        console.warn('[Subscription] Failed to parse status response:', this.buildSubscriptionDiagnostics(headers, 'parse', response.status, contentType));
        return {
          isSubscribed: false,
          status: 'error',
          isTrialing: false,
          trialDaysRemaining: 0,
          canTrial: false,
          checkedAt,
          lastError: message,
          source: 'parse',
          httpStatus: response.status,
        };
      }

      if (!response.ok) {
        console.warn('[Subscription] Status HTTP error:', this.buildSubscriptionDiagnostics(headers, 'http', response.status, contentType));
        return {
          isSubscribed: false,
          status: 'error',
          isTrialing: false,
          trialDaysRemaining: 0,
          canTrial: false,
          checkedAt,
          lastError: data?.error || data?.reason || `Subscription check failed (${response.status})`,
          hint: data?.hint,
          source: 'http',
          httpStatus: response.status,
        };
      }

      const rawPlan = typeof data.plan === 'string'
        ? data.plan
        : typeof data.type === 'string'
          ? data.type
          : undefined;
      const normalizedType = rawPlan?.includes('business')
        ? 'business_plus'
        : rawPlan?.includes('year')
          ? 'yearly'
          : rawPlan?.includes('month') || rawPlan === 'individual'
            ? 'monthly'
            : undefined;
      const rawStatus: SubscriptionStatus['status'] = typeof data.status === 'string'
        ? (data.status as SubscriptionStatus['status'])
        : data.active
          ? 'active'
          : data.trialing
            ? 'trialing'
            : (data.expiresAt || data.trialEndsAt)
              ? 'expired'
              : 'none';

      const isSubscribed = Boolean(data.active || data.trialing);
      const status: SubscriptionStatus = {
        isSubscribed,
        status: rawStatus,
        type: normalizedType,
        rawPlan,
        expiresAt: data.expiresAt,
        isTrialing: Boolean(data.trialing),
        trialDaysRemaining: data.trialEndsAt ? Math.max(0, Math.ceil((new Date(data.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0,
        canTrial: !isSubscribed,
        checkedAt,
        source: 'api',
        isBusinessPlus: Boolean(rawPlan?.includes('business')),
        accessSource: data.accessSource,
        paymentRequired: data.paymentRequired === true,
        businessSeat: data.businessSeat || null,
        starsPaywall: data.starsPaywall || null,
      };
      
      this.cachedStatus = status;
      this.cacheTimestamp = Date.now();
      if (status.isSubscribed || status.status === 'trialing') {
        this.rememberGraceStatus(status);
      } else {
        this.clearGraceStatus();
      }
      
      return status;
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return {
        isSubscribed: false,
        status: 'error',
        isTrialing: false,
        trialDaysRemaining: 0,
        canTrial: false,
        checkedAt,
        lastError: error instanceof Error ? error.message : 'Failed to verify subscription',
        source: 'network',
      };
    }
  }

  /**
   * Check if user is subscribed (active or trialing)
   */
  async isSubscribed(): Promise<boolean> {
    const status = await this.getStatus();
    return status.isSubscribed;
  }

  async createStarsInvoice(options: {
    productType: 'individual_monthly' | 'individual_yearly' | 'business_seats_30d';
    seatCount?: number;
  }): Promise<StarsInvoiceResult> {
    const response = await fetch('/api/stars/invoices', {
      method: 'POST',
      headers: {
        ...this.getUserHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success || !data?.invoice) {
      throw new Error(data?.error || 'Failed to create Telegram Stars invoice');
    }
    return data.invoice as StarsInvoiceResult;
  }

  async pollForActiveAccess(timeoutMs = 45000): Promise<SubscriptionStatus> {
    const started = Date.now();
    let lastStatus = await this.getStatus(true);
    while (!lastStatus.isSubscribed && Date.now() - started < timeoutMs) {
      await sleep(2500);
      lastStatus = await this.getStatus(true);
    }
    return lastStatus;
  }

  /**
   * Validate a promo code
   */
  async validatePromo(code: string): Promise<PromoValidation> {
    try {
      const response = await fetch(`${API_BASE}/promo?code=${encodeURIComponent(code)}`, {
        headers: this.getUserHeaders()
      });
      
      return await response.json();
    } catch (error) {
      console.error('Failed to validate promo:', error);
      return { valid: false, error: 'Failed to validate promo code' };
    }
  }

  /**
   * Apply a promo code to user's account
   */
  async applyPromo(code: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_BASE}/promo`, {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });
      
      return await response.json();
    } catch (error) {
      console.error('Failed to apply promo:', error);
      return { success: false, error: 'Failed to apply promo code' };
    }
  }

  /**
   * Initiate checkout - Opens Telegram bot for secure payment
   */
  async initiateCheckout(plan: PlanId): Promise<void> {
    // Open the bot with /subscribe command
    // This is the secure way to handle payments in Telegram Mini Apps
    const botUsername = 'vamosar_race_bot';
    const botUrl = `https://t.me/${botUsername}?start=subscribe_${plan}`;
    
    // Try multiple methods to open the bot
    const tg = (window as any).Telegram?.WebApp;
    
    try {
      // Method 1: Use Telegram's openTelegramLink (most reliable in Telegram)
      if (tg?.openTelegramLink) {
        console.log('[Subscription] Opening via openTelegramLink:', botUrl);
        tg.openTelegramLink(botUrl);
        return;
      }
      
      // Method 2: Use openLink for external URLs
      if (tg?.openLink) {
        console.log('[Subscription] Opening via openLink:', botUrl);
        tg.openLink(botUrl, { try_instant_view: false });
        return;
      }
      
      // Method 3: Direct window.open fallback
      console.log('[Subscription] Opening via window.open:', botUrl);
      window.open(botUrl, '_blank');
    } catch (error) {
      console.error('[Subscription] Failed to open bot:', error);
      // Ultimate fallback - try window.location
      window.location.href = botUrl;
    }
  }

  /**
   * Get pricing plans
   */
  getPricingPlans(): PricingPlan[] {
    return [
      {
        id: 'individual_monthly',
        name: 'Individual Monthly',
        price: 10.00,
        interval: 'month',
        trialDays: 7,
        tier: 'individual',
        features: [
          '🗺️ 1.4 Million Wells & Facilities (NM + TX)',
          '🔊 Voice Navigation & Turn-by-Turn Directions',
          '📋 Pre/Post Trip Vehicle Inspections',
          '🦺 Job Safety Analysis (JSA) with Crew Sign-Off',
          '👥 Crew Management & Marching Orders',
          '⏱️ Work Timer & Timesheets',
          '🤖 AI Assistant (Grok)',
          '⚠️ Road Hazard Reporting & Alerts',
          '🌤️ Weather, Air Quality & Pollen Data',
          '🔐 Cert & License Vault',
          '📸 Expense Receipts & Photo Ticket OCR',
          '🌮 Community Routes, Poop Pins & Taco Trucks',
          '📱 Offline Maps & Background Sync (PWA)',
          '🌐 English & Spanish',
          '💚 10% goes to Weekend Hunger Initiative'
        ]
      },
      {
        id: 'individual_yearly',
        name: 'Individual Annual',
        price: 100.00,
        originalPrice: 120.00,
        interval: 'year',
        trialDays: 7,
        popular: true,
        savings: '2 months FREE!',
        tier: 'individual',
        features: [
          '🗺️ 1.4 Million Wells & Facilities (NM + TX)',
          '🔊 Voice Navigation & Turn-by-Turn Directions',
          '📋 Pre/Post Trip Vehicle Inspections',
          '🦺 Job Safety Analysis (JSA) with Crew Sign-Off',
          '👥 Crew Management & Marching Orders',
          '⏱️ Work Timer & Timesheets',
          '🤖 AI Assistant (Grok)',
          '⚠️ Road Hazard Reporting & Alerts',
          '🌤️ Weather, Air Quality & Pollen Data',
          '🔐 Cert & License Vault',
          '📸 Expense Receipts & Photo Ticket OCR',
          '🌮 Community Routes, Poop Pins & Taco Trucks',
          '📱 Offline Maps & Background Sync (PWA)',
          '🌐 English & Spanish',
          '🎁 2 months FREE ($20 savings)',
          '💚 20% goes to Weekend Hunger Initiative'
        ]
      }
    ];
  }

  /**
   * Get Business pricing display info
   */
  getBusinessPricingInfo() {
    return {
      tiers: BUSINESS_TIERS,
      charityPercent: 20,
      charityName: 'Weekend Hunger Initiative',
      features: [
        '🏢 Everything in Field Professional',
        '🖥️ Enterprise Portal & Fleet Management',
        '✅ Company Verification Badge',
        '📱 Bulk License Management',
        '💼 Jobs Board Posting (hire workers)',
        '🎯 Dedicated Support Channel',
        '💰 Volume Discounts (100+ users)',
        '💚 10% monthly / 20% annual to Weekend Hunger Initiative'
      ]
    };
  }

  /**
   * Get Business Plus pricing plans (legacy - use getBusinessPricingInfo instead)
   */
  getBusinessPlusPlan(): PricingPlan {
    return {
      id: 'business_starter',
      name: 'Business Starter',
      price: 7.00,
      interval: 'month',
      trialDays: 7,
      tier: 'business',
      badge: 'TEAM',
      minUsers: 5,
      maxUsers: 25,
      charityPercent: 20,
      features: [
        '🏢 Everything in Individual',
        '👥 5-25 user licenses',
        '💼 Company verification badge',
        '🎯 Priority enterprise support',
        '💚 20% goes to Weekend Hunger Initiative'
      ]
    };
  }

  /**
   * Initiate Stripe checkout for individual plans
   */
  async initiateStripeCheckout(planId: 'individual_monthly' | 'individual_yearly', successUrl?: string, cancelUrl?: string): Promise<{ checkoutUrl?: string; error?: string }> {
    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          planId,
          successUrl: successUrl || `${window.location.origin}/?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`
        })
      });

      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Stripe checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Initiate Stripe checkout for business plans
   */
  async initiateBusinessCheckout(
    userCount: number,
    billingCycle: 'monthly' | 'yearly' = 'yearly',
    successUrl?: string,
    cancelUrl?: string,
    customBotSetup?: {
      includeCustomBotSetup?: boolean;
      includeApplicationSupport?: boolean;
      botName?: string;
      contactEmail?: string;
      useCase?: string;
    }
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    const pricing = calculateBusinessPrice(userCount, billingCycle);
    if (!pricing) {
      return { error: `Minimum ${MIN_BUSINESS_LICENSES} users required for business plans` };
    }
    if (userCount > MAX_BUSINESS_LICENSES) {
      return { error: `Maximum ${MAX_BUSINESS_LICENSES.toLocaleString()} users supported in self-serve checkout` };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          planId: `business_${pricing.tier.id}`,
          userCount,
          billingCycle,
          includeCustomBotSetup: customBotSetup?.includeCustomBotSetup === true,
          includeApplicationSupport: customBotSetup?.includeApplicationSupport === true,
          botName: customBotSetup?.botName || '',
          contactEmail: customBotSetup?.contactEmail || '',
          useCase: customBotSetup?.useCase || '',
          successUrl: successUrl || `${window.location.origin}/?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`
        })
      });

      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Business checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Initiate Stripe one-time checkout for Custom Bot Integration ($1,000)
   */
  async initiateCustomBotCheckout(
    metadata: { orgName: string; botName: string; contactEmail: string; useCase: string },
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: 'custom_bot_setup',
          successUrl: successUrl || `${window.location.origin}/app?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
          ...metadata,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Custom bot checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Initiate Stripe one-time checkout for Cyber Class seats ($95/head).
   */
  async initiateCyberClassCheckout(
    metadata: {
      attendeeCount: number;
      contactName: string;
      contactEmail: string;
      companyName?: string;
      notes?: string;
    },
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    const attendeeCount = Math.trunc(Number(metadata.attendeeCount || 0));
    if (!Number.isFinite(attendeeCount) || attendeeCount < 1 || attendeeCount > 100) {
      return { error: 'Cyber class attendee count must be between 1 and 100' };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: 'cyber_class_6h',
          attendeeCount,
          contactName: metadata.contactName || '',
          contactEmail: metadata.contactEmail || '',
          companyName: metadata.companyName || '',
          notes: metadata.notes || '',
          successUrl: successUrl || `${window.location.origin}/?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Cyber class checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Initiate Stripe one-time checkout for landing-page Business Services.
   */
  async initiateBusinessServiceCheckout(
    metadata: {
      planId: BusinessServicePlanId;
      quantity: number;
      contactName: string;
      contactEmail: string;
      companyName?: string;
      phone?: string;
      notes?: string;
    },
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkoutUrl?: string; error?: string }> {
    const quantity = Math.trunc(Number(metadata.quantity || 0));
    if (!Number.isFinite(quantity) || quantity < 1 || quantity > 100) {
      return { error: 'Business service quantity must be between 1 and 100' };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: metadata.planId,
          quantity,
          contactName: metadata.contactName || '',
          contactEmail: metadata.contactEmail || '',
          companyName: metadata.companyName || '',
          phone: metadata.phone || '',
          notes: metadata.notes || '',
          successUrl: successUrl || `${window.location.origin}/?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`,
        }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Business service checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Check if user has Business Plus subscription
   */
  async isBusinessPlus(): Promise<boolean> {
    const status = await this.getStatus();
    return status.isBusinessPlus === true || status.type === 'business_plus';
  }

  /**
   * Get subscription benefits for display
   */
  getSubscriptionBenefits(): { icon: string; title: string; description: string }[] {
    return [
      {
        icon: '🗺️',
        title: 'Full Navigation',
        description: 'Navigate to any well, SWD, rig, or facility in New Mexico'
      },
      {
        icon: '📱',
        title: 'Offline Access',
        description: 'Download entire basins for offline use in remote areas'
      },
      {
        icon: '🔊',
        title: 'Voice Commands',
        description: 'Hands-free voice search and turn-by-turn directions'
      },
      {
        icon: '📢',
        title: 'Basin Channels',
        description: 'Access private channels for road conditions & safety alerts'
      },
      {
        icon: '🛤️',
        title: 'Caliche Routes',
        description: 'Community-shared routes to avoid bad roads'
      },
      {
        icon: '📸',
        title: 'Site Photos',
        description: 'View and share photos to identify locations visually'
      }
    ];
  }

  /**
   * Clear cached status (call after subscription changes)
   */
  clearCache(): void {
    this.cachedStatus = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Link a Stripe purchase (made as guest) to the logged-in Telegram user by email.
   */
  async linkPurchaseByEmail(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/subscription/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getUserHeaders(),
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { success: false, error: data.error || 'Failed to link purchase' };
      }
      return { success: true };
    } catch (err) {
      console.error('Failed to link purchase:', err);
      return { success: false, error: 'Failed to link purchase' };
    }
  }

  /**
   * Link a Stripe purchase to the current Telegram account using checkout metadata.
   */
  async linkPurchaseByTelegram(): Promise<{ success: boolean; error?: string }> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const res = await fetch('/api/subscription/link', {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...this.getUserHeaders(),
        },
        body: JSON.stringify({ method: 'telegram' }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { success: false, error: data.error || 'Failed to link purchase' };
      }
      return { success: true };
    } catch (err) {
      console.error('Failed to link purchase by Telegram:', err);
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { success: false, error: 'Telegram linking timed out. Close and reopen the mini app, then try again.' };
      }
      return { success: false, error: 'Failed to link purchase' };
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * Fetch license keys for a purchase email (business seats).
   */
  async fetchLicensesByEmail(email: string): Promise<{ licenses?: any[]; subscriptions?: any[]; error?: string }> {
    try {
      const res = await fetch('/api/subscription/licenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { error: data.error || 'Failed to load licenses' };
      }
      return { licenses: data.licenses, subscriptions: data.subscriptions };
    } catch (err) {
      console.error('Failed to fetch licenses:', err);
      return { error: 'Failed to load licenses' };
    }
  }

  /**
   * Claim a license key for business plans.
   */
  async claimLicense(licenseKey: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/subscription/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getUserHeaders(),
        },
        body: JSON.stringify({ licenseKey }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        return { success: false, error: data.error || 'Failed to claim license' };
      }
      this.clearCache();
      return { success: true };
    } catch (err) {
      console.error('Failed to claim license:', err);
      return { success: false, error: 'Failed to claim license' };
    }
  }
}

export const subscriptionService = new SubscriptionService();
